import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Alert,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import {
  useLifts,
  useDeleteLift,
  getMaintenanceStatusColor,
  type Lift,
  type LiftType,
  type MaintenanceStatus,
} from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { LIFT_TYPE_OPTIONS, liftTypeLabel, maintenanceStatusLabel } from '@/utils/liftLabels';
import { LiftFormDialog } from './LiftFormDialog';
import { MaintenanceStatusDialog } from './MaintenanceStatusDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/common';
import { useAuthStore } from '@/stores/authStore';

export function LiftsList() {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Lift mutations require equipment.manage server-side; hide what would 403.
  const canManageEquipment = hasPermission('equipment.manage');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<LiftType | ''>('');
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | ''>('');
  const [editLift, setEditLift] = useState<Lift | null>(null);
  const [deleteLift, setDeleteLift] = useState<Lift | null>(null);
  const [statusLift, setStatusLift] = useState<Lift | null>(null);

  // DELETE /lifts/:id is a soft delete (isActive=false) and GET /lifts only
  // filters on isActive when asked, so without this a removed lift came back
  // looking untouched right after "deleted" (#4092).
  const { data, isLoading, isError } = useLifts({
    isActive: true,
    q: search || undefined,
    type: typeFilter || undefined,
    maintenanceStatus: statusFilter || undefined,
    limit: 50,
  });

  const deleteMutation = useDeleteLift();

  const lifts = data?.pages.flatMap((page) => page.data) ?? [];

  const handleConfirmDelete = async () => {
    if (!deleteLift) return;

    try {
      await deleteMutation.mutateAsync(deleteLift.id);
      notify.success(t('lifts.deleted'), t('lifts.deletedDesc', { name: deleteLift.name }));
      setDeleteLift(null);
    } catch {
      notify.error(tc('error'), t('lifts.deleteFailed'));
    }
  };

  const formatCapacity = (kg: number) => {
    if (kg >= 1000) {
      return `${(kg / 1000).toFixed(1)}t`;
    }
    return `${String(kg)} kg`;
  };

  const formatDimensions = (lift: Lift) => {
    return `${String(lift.lengthCm)}x${String(lift.widthCm)} cm`;
  };

  const sortCompareFn = (a: Lift, b: Lift, key: string): number => {
    switch (key) {
      case 'identifier':
        return a.identifier.localeCompare(b.identifier, 'fi');
      case 'name':
        return a.name.localeCompare(b.name, 'fi');
      case 'type':
        return a.type.localeCompare(b.type);
      case 'capacity':
        return a.capacityKg - b.capacityKg;
      case 'status': {
        const order: Record<MaintenanceStatus, number> = { operational: 0, needs_maintenance: 1, out_of_service: 2 };
        return order[a.maintenanceStatus] - order[b.maintenanceStatus];
      }
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<Lift>[] = [
    {
      header: t('lifts.identifier'),
      sortKey: 'identifier',
      renderCell: (lift) => (
        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
          {lift.identifier}
        </Typography>
      ),
    },
    {
      header: t('lifts.name'),
      sortKey: 'name',
      renderCell: (lift) => (
        <Typography variant="body2">
          {lift.name}
        </Typography>
      ),
    },
    {
      header: t('lifts.type'),
      sortKey: 'type',
      renderCell: (lift) => (
        <Typography variant="body2" color="text.secondary">
          {liftTypeLabel(t, lift.type)}
        </Typography>
      ),
    },
    {
      header: t('lifts.capacity'),
      sortKey: 'capacity',
      renderCell: (lift) => (
        <Typography variant="body2">
          {formatCapacity(lift.capacityKg)}
        </Typography>
      ),
    },
    {
      header: t('lifts.dimensions'),
      renderCell: (lift) => (
        <Typography variant="body2" color="text.secondary">
          {formatDimensions(lift)}
        </Typography>
      ),
    },
    {
      header: t('lifts.location'),
      renderCell: (lift) => (
        <Typography variant="body2">
          {lift.workshopLocation ?? '-'}
        </Typography>
      ),
    },
    {
      header: tc('status'),
      sortKey: 'status',
      align: 'center',
      renderCell: (lift) => (
        <Chip
          label={maintenanceStatusLabel(t, lift.maintenanceStatus)}
          size="small"
          color={getMaintenanceStatusColor(lift.maintenanceStatus)}
          variant="outlined"
        />
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (lift) => canManageEquipment && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          <Tooltip title={t('lifts.updateStatus')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setStatusLift(lift); }}
              color="primary"
              aria-label={t('lifts.updateStatus')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <BuildIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('edit')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setEditLift(lift); }}
              aria-label={tc('edit')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('delete')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setDeleteLift(lift); }}
              color="error"
              aria-label={tc('delete')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        {t('lifts.loadFailed')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        // Bottom-aligned: a label-less search field is 16px shorter than a
        // labelled one (the theme reserves that room above the box), so the
        // default stretch leaves the search box out of line (#2259).
        sx={{ mb: 3, alignItems: 'flex-end' }}
      >
        <TextField
          placeholder={t('lifts.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          size="small"
          sx={{ width: 250 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('lifts.type')}</InputLabel>
          <Select
            value={typeFilter}
            label={t('lifts.type')}
            onChange={(e) => { setTypeFilter(e.target.value as LiftType | ''); }}
          >
            <MenuItem value="">{t('lifts.allTypes')}</MenuItem>
            {LIFT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{t(opt.labelKey)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{tc('status')}</InputLabel>
          <Select
            value={statusFilter}
            label={tc('status')}
            onChange={(e) => { setStatusFilter(e.target.value as MaintenanceStatus | ''); }}
          >
            <MenuItem value="">{t('lifts.allStatuses')}</MenuItem>
            <MenuItem value="operational">{t('lifts.statusOperational')}</MenuItem>
            <MenuItem value="needs_maintenance">{t('lifts.statusNeedsMaintenance')}</MenuItem>
            <MenuItem value="out_of_service">{t('lifts.statusOutOfService')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <DataTable<Lift>
        columns={columns}
        data={lifts}
        getRowKey={(lift) => lift.id}
        onRowClick={(lift) => { setEditLift(lift); }}
        sortCompareFn={sortCompareFn}
        defaultSortKey="identifier"
        loading={isLoading}
        emptyMessage={t('lifts.noLifts')}
        ariaLabel={t('lifts.title')}
        renderMobileCard={(lift) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                  {lift.identifier}
                </Typography>
                <Typography variant="body2">
                  {lift.name}
                </Typography>
              </Box>
              <Chip
                label={maintenanceStatusLabel(t, lift.maintenanceStatus)}
                size="small"
                color={getMaintenanceStatusColor(lift.maintenanceStatus)}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {liftTypeLabel(t, lift.type)} · {formatCapacity(lift.capacityKg)} · {formatDimensions(lift)}
                </Typography>
              </Box>
              {canManageEquipment && (
                <Box>
                  <Tooltip title={t('lifts.updateStatus')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setStatusLift(lift); }} color="primary" aria-label={t('lifts.updateStatus')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <BuildIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tc('edit')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditLift(lift); }} aria-label={tc('edit')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tc('delete')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteLift(lift); }} color="error" aria-label={tc('delete')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        )}
      />

      {/* Edit Dialog */}
      {editLift && (
        <LiftFormDialog
          open={!!editLift}
          lift={editLift}
          onClose={() => { setEditLift(null); }}
        />
      )}

      {/* Maintenance Status Dialog */}
      {statusLift && (
        <MaintenanceStatusDialog
          open={!!statusLift}
          lift={statusLift}
          onClose={() => { setStatusLift(null); }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteLift}
        title={t('lifts.deleteTitle')}
        message={t('lifts.deleteConfirm', { name: deleteLift?.name })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => { setDeleteLift(null); }}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
